import { SERVICES } from '@/lib/constants'
import { fetchServicePageBySlug, fetchServicePages } from './cmsService'
import { toSlug } from './helpers'
import type { ServicePageContent } from './types'

/** Legacy route IDs → alternate CMS slug when `slug` field differs from URL segment */
const ROUTE_ALTERNATE_SLUGS: Record<string, string> = Object.fromEntries(
  SERVICES.map((s) => [s.id, toSlug(s.title)])
)

/**
 * Canonical `/services/:id` → actual Strapi `slug` values (staging CMS).
 * Tried after URL slug + title-derived slug when filtering `service-pages`.
 */
const ROUTE_CMS_SLUG_ALIASES: Record<string, string[]> = {
  'odoo-erp': ['odoo-erp-solution', 'ODOO-ERP-SOLUTION'],
  'custom-solutions': ['custom-digital-solutions'],
  'ai-solutions': ['AI&WHATSAPP-AUTOMATION', 'ai-and-whatsapp-automation'],
}

/** Strapi slug → canonical route id used in app links */
const CANONICAL_ROUTE_FROM_CMS_SLUG: Record<string, string> = {
  'odoo-erp-solution': 'odoo-erp',
  'custom-digital-solutions': 'custom-solutions',
  'ai-and-whatsapp-automation': 'ai-solutions',
}

const ROUTE_TYPO_FIX: Record<string, string> = {
  'ai-soultion': 'ai-solutions',
  'ai-solution': 'ai-solutions',
  'ai-tools': 'ai-solutions',
  'odoo-erp-soultion': 'odoo-erp',
  'odoo-erp-solution': 'odoo-erp',
  'odo-erp-soultion': 'odoo-erp',
  'odo-erp-solution': 'odoo-erp',
}

export function normalizeServiceRouteId(routeId?: string): string {
  const slug = toSlug(routeId ?? '')
  return ROUTE_TYPO_FIX[slug] ?? slug
}

/** Build `/services/:slug` from CMS service card `slug` (preferred). */
export function servicePagePathFromSlug(slug?: string): string | null {
  const normalized = toSlug(slug ?? '')
  if (!normalized) return null
  const canonical = CANONICAL_ROUTE_FROM_CMS_SLUG[normalized] ?? normalizeServiceRouteId(normalized)
  if (!canonical) return null
  return `/services/${canonical}`
}

export type ServiceCardTarget =
  | { type: 'internal'; to: string }
  | { type: 'external'; href: string }

/**
 * Where a homepage service card should navigate.
 * Slug → `/services/:slug`; if slug is empty, uses CMS `link` (internal or external).
 */
export function serviceCardTargetFromItem(item: {
  slug?: string
  link?: string
}): ServiceCardTarget | null {
  const slugPath = servicePagePathFromSlug(item.slug)
  if (slugPath) return { type: 'internal', to: slugPath }

  const link = item.link?.trim()
  if (!link) return null

  if (/^(https?|mailto|tel):/i.test(link)) {
    return { type: 'external', href: link }
  }

  const fromLink = servicePagePathFromLink(link)
  if (fromLink) return { type: 'internal', to: fromLink }

  if (link.startsWith('#')) {
    return { type: 'external', href: link }
  }

  if (link.startsWith('/')) {
    return { type: 'internal', to: link }
  }

  return null
}

/** Internal app path only — null for external/mailto/hash links (use `serviceCardTargetFromItem`). */
export function servicePagePathFromItem(item: { slug?: string; link?: string }): string | null {
  const target = serviceCardTargetFromItem(item)
  if (!target || target.type !== 'internal') return null
  return target.to
}

/** Turn CMS/homepage service card Link into a canonical `/services/:slug` path. */
export function servicePagePathFromLink(link?: string): string | null {
  const trimmed = link?.trim()
  if (!trimmed) return null

  let slug = ''
  if (trimmed.startsWith('/services/')) {
    slug = trimmed.slice('/services/'.length).split(/[?#]/)[0] ?? ''
  } else if (/^https?:\/\//i.test(trimmed)) {
    try {
      const pathname = new URL(trimmed).pathname
      if (pathname.startsWith('/services/')) {
        slug = pathname.slice('/services/'.length).split(/[?#]/)[0] ?? ''
      }
    } catch {
      return null
    }
  } else {
    slug = trimmed.replace(/^\//, '').split(/[?#]/)[0] ?? ''
  }

  if (!slug) return null
  return `/services/${normalizeServiceRouteId(slug)}`
}

/** Slugs to try against `filters[slug][$eq]` (URL slug, canonical slug, legacy title slug). */
export function cmsSlugsForRoute(routeId?: string): string[] {
  if (!routeId?.trim()) return []

  const raw = toSlug(routeId)
  const canonical = normalizeServiceRouteId(routeId)
  const alternate = ROUTE_ALTERNATE_SLUGS[canonical] ?? ROUTE_ALTERNATE_SLUGS[raw]
  const aliases = ROUTE_CMS_SLUG_ALIASES[canonical] ?? []
  const slugs: string[] = []
  for (const s of [raw, canonical, alternate, ...aliases]) {
    if (s && !slugs.includes(s)) slugs.push(s)
  }
  return slugs
}

/**
 * Load service page per CMS API spec: filter by slug, no fallback to first entry.
 * If slug is unset in Strapi, falls back to matching within the full list by route/title only.
 */
export async function loadServicePageForRoute(
  routeId?: string
): Promise<ServicePageContent | null> {
  if (!routeId?.trim()) return null

  const slugs = cmsSlugsForRoute(routeId)
  const bySlugResults = await Promise.all(slugs.map((slug) => fetchServicePageBySlug(slug)))
  const matched = bySlugResults.find((page) => page != null)
  if (matched) return matched

  const pages = await fetchServicePages()
  return resolveServicePageInList(pages, routeId)
}

/** Client-side match only when CMS `slug` filter returned nothing (e.g. slug not set in Strapi). */
export function resolveServicePageInList(
  pages: ServicePageContent[],
  routeId?: string
): ServicePageContent | null {
  if (!pages.length || !routeId) return null

  const slugCandidates = cmsSlugsForRoute(routeId)
  const byKnownSlug = pages.find((p) => slugCandidates.includes(p.slug))
  if (byKnownSlug) return byKnownSlug

  const route = normalizeServiceRouteId(routeId)
  const alternate = ROUTE_ALTERNATE_SLUGS[route]
  const byTitle = pages.find(
    (p) => toSlug(p.title) === route || (alternate != null && toSlug(p.title) === alternate)
  )
  if (byTitle) return byTitle

  return null
}
